'use client';

import { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { cn } from '@/lib/utils';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { Logo } from '@/components/common/Logo';

const navigation = [
  { name: 'About', href: '/about' },
  { name: 'Features', href: '/features' },
  { name: 'How To', href: '/how-to' },
  { name: 'Pricing', href: '/pricing' },
  { name: 'FAQ', href: '/faq' },
  { name: 'Contact', href: '/contact' },
];

const legalLinks = [
  { name: 'Terms', href: '/terms' },
  { name: 'Privacy', href: '/privacy' },
];

export function StaticPageHeader() {
  const pathname = usePathname();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Don't redirect to auth pages or legal pages
  const redirectUrl =
    pathname.startsWith('/auth/') ||
    pathname.startsWith('/terms') ||
    pathname.startsWith('/privacy')
      ? undefined
      : pathname;

  const getAuthUrl = (path: string) => {
    if (!redirectUrl) return path;
    return `${path}?redirectUrl=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <header className="sticky top-0 z-40 w-full bg-white/90 shadow-sm backdrop-blur-lg transition-all duration-300">
      <div className="layout flex h-16 items-center justify-between sm:h-20">
        {/* Logo and Navigation */}
        <div className="flex items-center gap-8">
          <Link href="/" className="group flex items-center">
            <Logo
              size="lg"
              className="transition-transform duration-300 group-hover:scale-105"
            />
          </Link>

          <nav className="hidden space-x-1 md:flex">
            {navigation.map((item) => (
              <Link
                key={item.name}
                href={item.href}
                className={cn(
                  'relative rounded-lg px-4 py-2 text-sm font-medium transition-all duration-300',
                  pathname === item.href
                    ? 'text-primary-600'
                    : 'text-gray-600 hover:text-primary-600'
                )}
              >
                {pathname === item.href && (
                  <motion.div
                    layoutId="nav-pill"
                    className="absolute inset-0 rounded-lg bg-primary-50"
                    transition={{ type: 'spring', duration: 0.6 }}
                  />
                )}
                <span className="relative z-10">{item.name}</span>
              </Link>
            ))}
          </nav>
        </div>

        {/* Legal Links & Auth Actions */}
        <div className="hidden items-center gap-6 md:flex">
          <div className="flex items-center space-x-4">
            {legalLinks.map((item) => (
              <Link
                key={item.name}
                href={item.href}
                className={cn(
                  'text-sm font-medium transition-colors hover:text-primary-600',
                  pathname === item.href ? 'text-primary-600' : 'text-gray-500'
                )}
              >
                {item.name}
              </Link>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <Link
              href={getAuthUrl('/auth/register')}
              className="rounded-lg bg-gradient-to-r from-primary-500 to-accent-blue px-4 py-2 text-sm font-semibold text-white shadow-md transition-all duration-300 hover:scale-105 hover:shadow-lg"
            >
              Get Started
            </Link>
          </div>
        </div>

        {/* Mobile menu button */}
        <button
          aria-label="Hamburger menu"
          className="rounded-lg p-2 text-gray-600 transition-colors hover:bg-gray-100 hover:text-primary-600 md:hidden"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={isMobileMenuOpen ? 'close' : 'menu'}
              initial={{ opacity: 0, rotate: -90 }}
              animate={{ opacity: 1, rotate: 0 }}
              exit={{ opacity: 0, rotate: 90 }}
              transition={{ duration: 0.2 }}
            >
              {isMobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </motion.div>
          </AnimatePresence>
        </button>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="border-b border-gray-100 bg-white md:hidden"
          >
            <div className="space-y-1 p-4">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  href={item.href}
                  className={cn(
                    'block rounded-lg px-4 py-2 text-base font-medium transition-all duration-300',
                    pathname === item.href
                      ? 'bg-primary-50 text-primary-600'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-primary-600'
                  )}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
              <div className="my-2 border-t border-gray-100"></div>
              {legalLinks.map((item) => (
                <Link
                  key={item.name}
                  href={item.href}
                  className={cn(
                    'block rounded-lg px-4 py-2 text-base font-medium transition-all duration-300',
                    pathname === item.href
                      ? 'bg-primary-50 text-primary-600'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-primary-600'
                  )}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
              <div className="mt-4 space-y-2 px-4">
                <Link
                  href={getAuthUrl('/auth/login')}
                  className="block rounded-lg bg-gray-50 px-4 py-3 text-center font-semibold text-gray-600 transition-all duration-300 hover:bg-gray-100 hover:text-primary-600"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Sign In
                </Link>
                <Link
                  href={getAuthUrl('/auth/register')}
                  className="block rounded-lg bg-gradient-to-r from-primary-500 to-accent-blue px-4 py-3 text-center font-semibold text-white shadow-md transition-all duration-300 hover:shadow-lg"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Get Started
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
